import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  NormalizedCacheObject,
} from "@apollo/client";
import { AuthenticationResult } from "@azure/msal-browser";

import { EHR_API_ENDPOINT } from "../environments/environment";

import { getValidatedEndpoint } from "./apollo-endpoint-provider/endpoint-helper";
import { getApolloHttpLink, getAuthLink } from "./apollo-links/apolloHttpLink";
import { retryLink } from "./apollo-links/retryLink";
import { telemetryErrorLink } from "./apollo-links/telemetryErrorLink";
import { telemetryLink } from "./apollo-links/telemetryLink";
import { cachePolicies } from "./cachePolicies";

export const getGlobalAnonymousClient = (): ApolloClient<
  NormalizedCacheObject
> => {
  return new ApolloClient({
    link: ApolloLink.from([
      telemetryLink,
      telemetryErrorLink,
      retryLink,
      getApolloHttpLink(getValidatedEndpoint(EHR_API_ENDPOINT)),
    ]),
    cache: new InMemoryCache(cachePolicies),
    defaultOptions: {
      watchQuery: { fetchPolicy: "network-only" },
      query: { errorPolicy: "all" },
      mutate: { awaitRefetchQueries: true },
    },
  });
};

export const getGlobalClient = (
  token: AuthenticationResult
): ApolloClient<NormalizedCacheObject> => {
  return new ApolloClient({
    link: ApolloLink.from([
      telemetryLink,
      telemetryErrorLink,
      retryLink,
      getAuthLink(token).concat(
        getApolloHttpLink(getValidatedEndpoint(EHR_API_ENDPOINT))
      ),
    ]),
    cache: new InMemoryCache(cachePolicies),
    defaultOptions: {
      watchQuery: { fetchPolicy: "network-only" },
      query: { errorPolicy: "all" },
      mutate: { awaitRefetchQueries: true },
    },
  });
};

/**
 * Returns anonymous regional apollo client
 * @param {string} endpoint - endpoint base URL
 */
export const getRegionalAnonymousClient = (
  endpoint = EHR_API_ENDPOINT
): ApolloClient<NormalizedCacheObject> => {
  return new ApolloClient({
    link: ApolloLink.from([
      telemetryLink,
      telemetryErrorLink,
      retryLink,
      getApolloHttpLink(getValidatedEndpoint(endpoint)),
    ]),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: { fetchPolicy: "cache-and-network" },
      query: { errorPolicy: "all" },
    },
  });
};

/**
 * Returns authenticated regional apollo client
 * @param {string} endpoint - endpoint base URL
 */
export const getRegionalClient = (
  endpoint = EHR_API_ENDPOINT,
  token: AuthenticationResult
): ApolloClient<NormalizedCacheObject> => {
  return new ApolloClient({
    link: ApolloLink.from([
      telemetryLink,
      telemetryErrorLink,
      retryLink,
      getAuthLink(token).concat(
        getApolloHttpLink(getValidatedEndpoint(endpoint))
      ),
    ]),
    cache: new InMemoryCache(cachePolicies),
    defaultOptions: {
      watchQuery: { fetchPolicy: "network-only" },
      query: { errorPolicy: "all" },
      mutate: { awaitRefetchQueries: true },
    },
  });
};
